// src/pages/SuperAdminPage.js - Pannello amministrazione agenti
import { useState, useEffect } from 'react';
import {
  Users,
  Plus,
  Edit,
  Trash2,
  RefreshCw,
  Shield,
  Activity,
  DollarSign,
  X,
  AlertTriangle,
  User,
  Search
} from 'lucide-react';
import ApiService from '../services/api';

const SuperAdminPage = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboardStats, setDashboardStats] = useState({});
  const [agents, setAgents] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 1 });
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Stati per modali
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    provinces: [],
    provincesInput: '', // Aggiungi campo per l'input raw
    canShareCustomers: false,
    isAdmin: false,
    isActive: true
  });

  // Carica statistiche dashboard
  const loadDashboardStats = async () => {
    try {
      const stats = await ApiService.getSuperAdminStats();
      setDashboardStats(stats);
    } catch (error) {
      console.error('Errore caricamento statistiche:', error);
    }
  };

  // Carica lista agenti
  const loadAgents = async (page = 1) => {
    setLoading(true);
    try {
      const response = await ApiService.getAgents(page, pagination.limit);
      setAgents(response.agents || []);
      setPagination(response.pagination || {});
    } catch (error) {
      console.error('Errore caricamento agenti:', error);
    } finally {
      setLoading(false);
    }
  };

  // Crea nuovo agente
  const handleCreateAgent = async () => {
    try {
      const response = await ApiService.createAgent(formData);
      
      // Mostra password temporanea
      alert(`Agente creato con successo!\nPassword temporanea: ${response.tempPassword}\n\nInvia queste credenziali all'agente via email sicura.`);
      
      resetForm();
      setShowCreateModal(false);
      loadAgents(pagination.page);
    } catch (error) {
      console.error('Errore creazione agente:', error);
      alert('Errore nella creazione dell\'agente: ' + error.message);
    }
  };

  // Aggiorna agente
  const handleUpdateAgent = async () => {
    try {
      await ApiService.updateAgent(selectedAgent.id, formData);
      alert('Agente aggiornato con successo!');
      resetForm();
      setShowEditModal(false);
      loadAgents(pagination.page);
    } catch (error) {
      console.error('Errore aggiornamento agente:', error);
      alert('Errore nell\'aggiornamento dell\'agente: ' + error.message);
    }
  };

  // Reset password agente
  const handleResetPassword = async (agent) => {
    if (!window.confirm(`Vuoi resettare la password di ${agent.name}?`)) return;
    
    try {
      const response = await ApiService.resetAgentPassword(agent.id);
      alert(`Password resettata!\nNuova password temporanea: ${response.tempPassword}\n\nInvia la nuova password all'agente via email sicura.`);
    } catch (error) {
      console.error('Errore reset password:', error);
      alert('Errore nel reset della password: ' + error.message);
    }
  };

  // Elimina agente
  const handleDeleteAgent = async () => {
    try {
      await ApiService.deleteAgent(selectedAgent.id);
      alert('Agente eliminato con successo!');
      setShowDeleteModal(false);
      setSelectedAgent(null);
      loadAgents(pagination.page);
    } catch (error) {
      console.error('Errore eliminazione agente:', error);
      alert('Errore nell\'eliminazione dell\'agente: ' + error.message);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      provinces: [],
      provincesInput: '',
      canShareCustomers: false,
      isAdmin: false,
      isActive: true
    });
    setSelectedAgent(null);
  };

  // Apri modal modifica
  const openEditModal = (agent) => {
    setSelectedAgent(agent);
    const provincesArray = agent.provinces || [];
    setFormData({
      name: agent.name,
      email: agent.email,
      provinces: provincesArray,
      provincesInput: provincesArray.join(', '),
      canShareCustomers: agent.canShareCustomers,
      isAdmin: agent.isAdmin,
      isActive: agent.isActive
    });
    setShowEditModal(true);
  };

  // Apri modal eliminazione
  const openDeleteModal = (agent) => {
    setSelectedAgent(agent);
    setShowDeleteModal(true);
  };

  // Gestione province input
  const handleProvincesChange = (value) => {
    // Salva il valore raw per permettere all'utente di digitare virgole
    setFormData({ 
      ...formData, 
      provincesInput: value,
      provinces: value.split(',').map(p => p.trim()).filter(p => p)
    });
  };

  // Filtra agenti per ricerca
  const filteredAgents = agents.filter(agent =>
    agent.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    agent.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    loadDashboardStats();
    loadAgents();
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                <Shield className="w-8 h-8 mr-3 text-red-600" />
                Amministrazione
              </h1>
              <p className="text-gray-600">Gestione agenti e statistiche sistema</p>
            </div>
          </div>

          {/* Tabs */}
          <div className="mt-6 border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('dashboard')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'dashboard'
                    ? 'border-red-500 text-red-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Dashboard
              </button>
              <button
                onClick={() => setActiveTab('agents')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'agents'
                    ? 'border-red-500 text-red-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Gestione Agenti
              </button>
            </nav>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-6 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-100 text-sm font-medium">Agenti Totali</p>
                    <p className="text-3xl font-bold">{dashboardStats.totalAgents || 0}</p>
                  </div>
                  <Users className="w-10 h-10 text-blue-200" />
                </div>
              </div>

              <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-6 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-100 text-sm font-medium">Clienti Totali</p>
                    <p className="text-3xl font-bold">{dashboardStats.totalCustomers || 0}</p>
                  </div>
                  <Users className="w-10 h-10 text-green-200" />
                </div>
              </div>

              <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg p-6 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-purple-100 text-sm font-medium">Ordini Questo Mese</p>
                    <p className="text-3xl font-bold">{dashboardStats.ordersThisMonth || 0}</p>
                  </div>
                  <Activity className="w-10 h-10 text-purple-200" />
                </div>
              </div>

              <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg p-6 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-orange-100 text-sm font-medium">Fatturato Mese</p>
                    <p className="text-3xl font-bold">
                      {formatCurrency(parseFloat(dashboardStats.revenueThisMonth) || 0)}
                    </p>
                  </div>
                  <DollarSign className="w-10 h-10 text-orange-200" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Agents Tab */}
        {activeTab === 'agents' && (
          <div className="space-y-6">
            {/* Header con ricerca e nuovo agente */}
            <div className="flex justify-between items-center">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Cerca agenti..."
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center space-x-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span>Nuovo Agente</span>
              </button>
            </div>

            {/* Lista Agenti */}
            <div className="bg-white rounded-lg shadow border">
              {loading ? (
                <div className="p-12 text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Caricamento agenti...</p>
                </div>
              ) : filteredAgents.length === 0 ? (
                <div className="p-12 text-center text-gray-500">
                  <Users className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Nessun agente trovato</h3>
                  <p className="text-sm">Crea il primo agente per iniziare</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Agente
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Province
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Statistiche
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Stato
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Azioni
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredAgents.map((agent) => (
                        <tr key={agent.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10">
                                <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                                  <User className="h-5 w-5 text-gray-500" />
                                </div>
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900 flex items-center">
                                  {agent.name}
                                  {agent.isAdmin && (
                                    <Shield className="ml-2 h-4 w-4 text-red-500" title="Amministratore" />
                                  )}
                                </div>
                                <div className="text-sm text-gray-500">{agent.email}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">
                              {agent.provinces && agent.provinces.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {agent.provinces.map((province, index) => (
                                    <span
                                      key={index}
                                      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                                    >
                                      {province}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-gray-400">Nessuna provincia</span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div>
                              <div>{agent._count?.orders || 0} ordini</div>
                              <div>{agent._count?.customers || 0} clienti</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex flex-col space-y-1">
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                agent.isActive 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {agent.isActive ? 'Attivo' : 'Inattivo'}
                              </span>
                              {agent.canShareCustomers && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                  Condivide clienti
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center space-x-2 justify-end">
                              <button
                                onClick={() => openEditModal(agent)}
                                className="text-blue-600 hover:text-blue-900"
                                title="Modifica"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleResetPassword(agent)}
                                className="text-orange-600 hover:text-orange-900"
                                title="Reset Password"
                              >
                                <RefreshCw className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => openDeleteModal(agent)}
                                className="text-red-600 hover:text-red-900"
                                title="Elimina"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Paginazione */}
              {pagination.pages > 1 && (
                <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-700">
                      Mostrando {(pagination.page - 1) * pagination.limit + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} di {pagination.total} agenti
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => loadAgents(pagination.page - 1)}
                        disabled={pagination.page <= 1}
                        className="px-3 py-2 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Precedente
                      </button>
                      <button
                        onClick={() => loadAgents(pagination.page + 1)}
                        disabled={pagination.page >= pagination.pages}
                        className="px-3 py-2 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Successivo
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modal Creazione Agente */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Nuovo Agente</h3>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="Nome e cognome"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="email@esempio.it"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Province (separate da virgola)
                </label>
                <input
                  type="text"
                  value={formData.provincesInput}
                  onChange={(e) => handleProvincesChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="Torino, Cuneo, Asti"
                />
              </div>

              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.canShareCustomers}
                    onChange={(e) => setFormData({ ...formData, canShareCustomers: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="text-sm">Può condividere clienti</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isAdmin}
                    onChange={(e) => setFormData({ ...formData, isAdmin: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="text-sm">Amministratore</span>
                </label>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Annulla
              </button>
              <button
                onClick={handleCreateAgent}
                disabled={!formData.name || !formData.email}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Crea Agente
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Modifica Agente */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Modifica Agente</h3>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Province (separate da virgola)
                </label>
                <input
                  type="text"
                  value={formData.provincesInput}
                  onChange={(e) => handleProvincesChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>

              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.canShareCustomers}
                    onChange={(e) => setFormData({ ...formData, canShareCustomers: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="text-sm">Può condividere clienti</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isAdmin}
                    onChange={(e) => setFormData({ ...formData, isAdmin: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="text-sm">Amministratore</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="text-sm">Attivo</span>
                </label>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  resetForm();
                }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Annulla
              </button>
              <button
                onClick={handleUpdateAgent}
                disabled={!formData.name || !formData.email}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Salva Modifiche
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Conferma Eliminazione */}
      {showDeleteModal && selectedAgent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center mb-4">
              <AlertTriangle className="w-6 h-6 text-red-600 mr-3" />
              <h3 className="text-lg font-semibold">Conferma Eliminazione</h3>
            </div>

            <p className="text-gray-700 mb-6">
              Sei sicuro di voler eliminare l'agente <strong>{selectedAgent.name}</strong>?
              <br />
              <span className="text-sm text-red-600">Questa azione non può essere annullata.</span>
            </p>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedAgent(null);
                }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Annulla
              </button>
              <button
                onClick={handleDeleteAgent}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Elimina Agente
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminPage;